import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  IconButton,
  Toolbar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Alert,
  Snackbar,
  Tooltip,
  Paper,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Add,
  Close,
  Edit,
  Delete,
  FileDownload,
  Circle,
  Event as EventIcon,
  Business,
  People,
  BeachAccess,
  AccessTime,
  LocationOn,
  Link as LinkIcon,
} from '@mui/icons-material';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import api from '../services/api';

const EVENT_TYPES = {
  sale: { label: 'Sale', color: '#1976d2', icon: Business },
  appointment: { label: 'Appointment', color: '#2e7d32', icon: People },
  internal: { label: 'Internal', color: '#ed6c02', icon: EventIcon },
  holiday: { label: 'Holiday', color: '#d32f2f', icon: BeachAccess },
};

const Calendar = () => {
  const [events, setEvents] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openEventModal, setOpenEventModal] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [filterTypes, setFilterTypes] = useState(['sale', 'appointment', 'internal', 'holiday']);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'internal',
    start_datetime: '',
    end_datetime: '',
    all_day: false,
    location: '',
    color: EVENT_TYPES.internal.color,
    estate_sale_id: null,
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchEvents(), fetchSales()]);
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Failed to load calendar data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const response = await api.get('/calendar/events');
      const eventsData = response.data?.events || response.data || [];
      setEvents(eventsData);
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  };

  const fetchSales = async () => {
    try {
      const response = await api.get('/sales/');
      const salesData = response.data?.sales || response.data || [];
      setSales(salesData);
    } catch (error) {
      console.error('Error fetching sales:', error);
    }
  };

  const formatEventsForCalendar = () => {
    return events
      .filter(event => filterTypes.includes(event.event_type))
      .map(event => ({
        id: event.id,
        title: event.title,
        start: event.start_datetime,
        end: event.end_datetime,
        allDay: event.all_day,
        backgroundColor: event.color || EVENT_TYPES[event.event_type]?.color || '#1976d2',
        borderColor: event.color || EVENT_TYPES[event.event_type]?.color || '#1976d2',
        extendedProps: {
          ...event,
        },
      }));
  };

  const getUpcomingEvents = () => {
    const now = new Date();
    const sevenDaysLater = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    return events
      .filter(event => {
        const eventDate = new Date(event.start_datetime);
        return eventDate >= now && eventDate <= sevenDaysLater;
      })
      .sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime))
      .slice(0, 5);
  };

  const handleDateClick = (arg) => {
    const clickedDate = new Date(arg.dateStr);
    const now = new Date();

    // Set time to current time if clicking today, otherwise use 9 AM
    if (clickedDate.toDateString() === now.toDateString()) {
      clickedDate.setHours(now.getHours(), now.getMinutes(), 0, 0);
    } else {
      clickedDate.setHours(9, 0, 0, 0);
    }

    const endDate = new Date(clickedDate);
    endDate.setHours(clickedDate.getHours() + 1);

    setFormData({
      title: '',
      description: '',
      event_type: 'internal',
      start_datetime: clickedDate.toISOString().slice(0, 16),
      end_datetime: endDate.toISOString().slice(0, 16),
      all_day: false,
      location: '',
      color: EVENT_TYPES.internal.color,
      estate_sale_id: null,
    });
    setSelectedEvent(null);
    setFormErrors({});
    setOpenEventModal(true);
  };

  const handleEventClick = (clickInfo) => {
    const event = clickInfo.event.extendedProps;
    setSelectedEvent({
      ...event,
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.start,
      end: clickInfo.event.end,
      allDay: clickInfo.event.allDay,
    });
    setOpenDetailModal(true);
  };

  const handleEditEvent = () => {
    if (!selectedEvent) return;

    setFormData({
      title: selectedEvent.title || '',
      description: selectedEvent.description || '',
      event_type: selectedEvent.event_type || 'internal',
      start_datetime: new Date(selectedEvent.start_datetime).toISOString().slice(0, 16),
      end_datetime: new Date(selectedEvent.end_datetime).toISOString().slice(0, 16),
      all_day: selectedEvent.all_day || false,
      location: selectedEvent.location || '',
      color: selectedEvent.color || EVENT_TYPES[selectedEvent.event_type]?.color || '#1976d2',
      estate_sale_id: selectedEvent.estate_sale_id || null,
    });
    setFormErrors({});
    setOpenDetailModal(false);
    setOpenEventModal(true);
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      await api.delete(`/calendar/events/${selectedEvent.id}`);
      showSnackbar('Event deleted successfully', 'success');
      setOpenDetailModal(false);
      setSelectedEvent(null);
      fetchEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      showSnackbar('Failed to delete event', 'error');
    }
  };

  const handleCloseEventModal = () => {
    setOpenEventModal(false);
    setSelectedEvent(null);
    setFormData({
      title: '',
      description: '',
      event_type: 'internal',
      start_datetime: '',
      end_datetime: '',
      all_day: false,
      location: '',
      color: EVENT_TYPES.internal.color,
      estate_sale_id: null,
    });
    setFormErrors({});
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectedEvent(null);
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.title?.trim()) errors.title = 'Title is required';
    if (!formData.start_datetime) errors.start_datetime = 'Start date/time is required';
    if (!formData.end_datetime) errors.end_datetime = 'End date/time is required';

    if (formData.start_datetime && formData.end_datetime) {
      const start = new Date(formData.start_datetime);
      const end = new Date(formData.end_datetime);
      if (end <= start) {
        errors.end_datetime = 'End time must be after start time';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const submitData = {
        ...formData,
        estate_sale_id: formData.estate_sale_id || null,
      };

      if (selectedEvent) {
        await api.put(`/calendar/events/${selectedEvent.id}`, submitData);
        showSnackbar('Event updated successfully', 'success');
      } else {
        await api.post('/calendar/events', submitData);
        showSnackbar('Event created successfully', 'success');
      }

      handleCloseEventModal();
      fetchEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Failed to save event';
      showSnackbar(errorMsg, 'error');
    }
  };

  const handleFormChange = (field, value) => {
    const updates = { [field]: value };

    // Update color when event type changes
    if (field === 'event_type') {
      updates.color = EVENT_TYPES[value]?.color || '#1976d2';
    }

    setFormData({ ...formData, ...updates });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }
  };

  const handleExportIcal = async () => {
    try {
      const response = await api.get('/calendar/export/ical', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'calendar.ics');
      document.body.appendChild(link);
      link.click();
      link.remove();

      showSnackbar('Calendar exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting calendar:', error);
      showSnackbar('Failed to export calendar', 'error');
    }
  };

  const handleFilterChange = (event, newFilters) => {
    if (newFilters.length > 0) {
      setFilterTypes(newFilters);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  const getSaleName = (saleId) => {
    const sale = sales.find(s => s.id === saleId);
    return sale?.name || 'Unknown Sale';
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <div>
          <Typography variant="h4" fontWeight="bold">
            Calendar & Events
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage sales, appointments, and events
          </Typography>
        </div>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<FileDownload />}
            onClick={handleExportIcal}
          >
            Export iCal
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              const now = new Date();
              const endDate = new Date(now.getTime() + 60 * 60 * 1000);
              setFormData({
                title: '',
                description: '',
                event_type: 'internal',
                start_datetime: now.toISOString().slice(0, 16),
                end_datetime: endDate.toISOString().slice(0, 16),
                all_day: false,
                location: '',
                color: EVENT_TYPES.internal.color,
                estate_sale_id: null,
              });
              setSelectedEvent(null);
              setFormErrors({});
              setOpenEventModal(true);
            }}
          >
            Add Event
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Calendar */}
        <Grid item xs={12} md={9}>
          <Card>
            <CardContent>
              {/* Filter Toolbar */}
              <Toolbar sx={{ px: 0, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ mr: 2 }}>
                  Filter:
                </Typography>
                <ToggleButtonGroup
                  value={filterTypes}
                  onChange={handleFilterChange}
                  size="small"
                  aria-label="event type filter"
                >
                  {Object.entries(EVENT_TYPES).map(([type, config]) => (
                    <ToggleButton key={type} value={type} aria-label={config.label}>
                      <Circle sx={{ fontSize: 12, mr: 1, color: config.color }} />
                      {config.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Toolbar>

              {/* Calendar */}
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridWeek,timeGridDay',
                }}
                events={formatEventsForCalendar()}
                editable={false}
                selectable={true}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                height="auto"
                eventTimeFormat={{
                  hour: 'numeric',
                  minute: '2-digit',
                  meridiem: 'short',
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          {/* Event Legend */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Event Types
              </Typography>
              <List dense>
                {Object.entries(EVENT_TYPES).map(([type, config]) => {
                  const Icon = config.icon;
                  return (
                    <ListItem key={type}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Circle sx={{ fontSize: 12, color: config.color }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={config.label}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </CardContent>
          </Card>

          {/* Upcoming Events */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Upcoming Events
              </Typography>
              <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                Next 7 days
              </Typography>
              <Divider sx={{ my: 1 }} />
              {getUpcomingEvents().length > 0 ? (
                <List dense>
                  {getUpcomingEvents().map((event) => (
                    <ListItem
                      key={event.id}
                      sx={{
                        px: 0,
                        py: 1,
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' },
                        borderRadius: 1,
                      }}
                      onClick={() => {
                        setSelectedEvent(event);
                        setOpenDetailModal(true);
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Circle
                          sx={{
                            fontSize: 12,
                            color: event.color || EVENT_TYPES[event.event_type]?.color
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={event.title}
                        secondary={
                          <>
                            {formatDateTime(event.start_datetime)}
                            {event.location && (
                              <Typography variant="caption" display="block" color="text.secondary">
                                {event.location}
                              </Typography>
                            )}
                          </>
                        }
                        primaryTypographyProps={{
                          variant: 'body2',
                          fontWeight: 'medium',
                          noWrap: true,
                        }}
                        secondaryTypographyProps={{
                          variant: 'caption',
                          component: 'div',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                  No upcoming events
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Event Create/Edit Modal */}
      <Dialog open={openEventModal} onClose={handleCloseEventModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            {selectedEvent ? 'Edit Event' : 'Create Event'}
            <IconButton onClick={handleCloseEventModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Title"
                value={formData.title}
                onChange={(e) => handleFormChange('title', e.target.value)}
                error={!!formErrors.title}
                helperText={formErrors.title}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required error={!!formErrors.event_type}>
                <InputLabel>Event Type</InputLabel>
                <Select
                  value={formData.event_type}
                  label="Event Type"
                  onChange={(e) => handleFormChange('event_type', e.target.value)}
                >
                  {Object.entries(EVENT_TYPES).map(([type, config]) => (
                    <MenuItem key={type} value={type}>
                      <Box display="flex" alignItems="center">
                        <Circle sx={{ fontSize: 12, mr: 1, color: config.color }} />
                        {config.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Color"
                type="color"
                value={formData.color}
                onChange={(e) => handleFormChange('color', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.all_day}
                    onChange={(e) => handleFormChange('all_day', e.target.checked)}
                  />
                }
                label="All Day Event"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Start Date & Time"
                type="datetime-local"
                value={formData.start_datetime}
                onChange={(e) => handleFormChange('start_datetime', e.target.value)}
                error={!!formErrors.start_datetime}
                helperText={formErrors.start_datetime}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="End Date & Time"
                type="datetime-local"
                value={formData.end_datetime}
                onChange={(e) => handleFormChange('end_datetime', e.target.value)}
                error={!!formErrors.end_datetime}
                helperText={formErrors.end_datetime}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location"
                value={formData.location}
                onChange={(e) => handleFormChange('location', e.target.value)}
                placeholder="Add location"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Link to Estate Sale</InputLabel>
                <Select
                  value={formData.estate_sale_id || ''}
                  label="Link to Estate Sale"
                  onChange={(e) => handleFormChange('estate_sale_id', e.target.value || null)}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {sales.map((sale) => (
                    <MenuItem key={sale.id} value={sale.id}>
                      {sale.name} - {sale.address}, {sale.city}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                multiline
                rows={4}
                placeholder="Add event description..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEventModal}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            {selectedEvent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Event Detail Modal */}
      <Dialog open={openDetailModal} onClose={handleCloseDetailModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <Circle
                sx={{
                  fontSize: 12,
                  color: selectedEvent?.color || EVENT_TYPES[selectedEvent?.event_type]?.color
                }}
              />
              <Typography variant="h6">{selectedEvent?.title}</Typography>
            </Box>
            <IconButton onClick={handleCloseDetailModal} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box>
              <Chip
                label={EVENT_TYPES[selectedEvent?.event_type]?.label || selectedEvent?.event_type}
                size="small"
                sx={{
                  bgcolor: selectedEvent?.color || EVENT_TYPES[selectedEvent?.event_type]?.color,
                  color: 'white',
                }}
              />
            </Box>

            {selectedEvent?.description && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body2">
                  {selectedEvent.description}
                </Typography>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Date & Time
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <AccessTime fontSize="small" color="action" />
                <Typography variant="body2">
                  {selectedEvent?.all_day ? (
                    <>
                      All Day - {new Date(selectedEvent.start_datetime).toLocaleDateString()}
                      {selectedEvent.end_datetime &&
                        ` to ${new Date(selectedEvent.end_datetime).toLocaleDateString()}`
                      }
                    </>
                  ) : (
                    <>
                      {formatDateTime(selectedEvent?.start_datetime)}
                      {selectedEvent?.end_datetime &&
                        ` - ${formatTime(selectedEvent.end_datetime)}`
                      }
                    </>
                  )}
                </Typography>
              </Box>
            </Box>

            {selectedEvent?.location && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Location
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <LocationOn fontSize="small" color="action" />
                  <Typography variant="body2">
                    {selectedEvent.location}
                  </Typography>
                </Box>
              </Box>
            )}

            {selectedEvent?.estate_sale_id && (
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Related Sale
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <LinkIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    {getSaleName(selectedEvent.estate_sale_id)}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleDeleteEvent}
            color="error"
            startIcon={<Delete />}
          >
            Delete
          </Button>
          <Box flex={1} />
          <Button onClick={handleCloseDetailModal}>Close</Button>
          <Button
            onClick={handleEditEvent}
            variant="contained"
            startIcon={<Edit />}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Calendar;
